import React from 'react'
import styled from 'styled-components'
import set from 'lodash.set'
import flatten from 'flatten'

import UnitElement from '../../../components/UnitElement'
import Table from '../../../../components/Table'
import {
  getSection,
  hasElement,
  sectionDisplayIndex,
  theoryQuestionsBySection,
} from '../../../../lib'

const Container = styled.div`
  height: 27%;
  overflow-y: scroll;
`

const getAllSectionMapping = section => {
  const mapping = new Map()
  for (const element of section.mapping) {
    mapping.set(`${element.type}-${element.index}`, element)
  }
  for (const subsection of section.subsections || []) {
    for (const element of getAllSectionMapping(subsection)) {
      mapping.set(`${element.type}-${element.index}`, element)
    }
  }
  for (const question of section.questions || []) {
    for (const element of question.mapping) {
      mapping.set(`${element.type}-${element.index}`, element)
    }
  }
  return Array.from(mapping.values())
}

const getLearnerGuideContentMapping = (section, sectionIndex, type, index) => {
  const mapping = []
  if (hasElement({ type, index }, section.mapping)) {
    mapping.push(sectionDisplayIndex(...sectionIndex))
  }
  for (const [subsectionIndex, subsection] of section.subsections.entries()) {
    if (hasElement({ type, index }, subsection.mapping)) {
      mapping.push(sectionDisplayIndex(...sectionIndex, subsectionIndex))
    }
    for (const [
      subsubsectionIndex,
      subsubsection,
    ] of subsection.subsections.entries()) {
      if (hasElement({ type, index }, subsubsection.mapping)) {
        mapping.push(
          sectionDisplayIndex(
            ...sectionIndex,
            subsectionIndex,
            subsubsectionIndex,
          ),
        )
      }
    }
  }
  return mapping
}

const getFormativeTheoryAssessmentMapping = (
  section,
  sectionIndex,
  type,
  index,
  multiChoice
) =>
  theoryQuestionsBySection(
    section.subsections,
    (multiChoice ? 'review-multi' : 'review'),
  ).reduce((mapping, question, questionIndex) => {
    if (hasElement({ type, index }, question.mapping)) {
      mapping.push(questionIndex + 1)
    }
    return mapping
  }, [])

const getSummativeTheoryAssessmentMapping = (
  section,
  sectionIndex,
  type,
  index,
  multiChoice,
) =>
  theoryQuestionsBySection(
    section.subsections,
    (multiChoice ? 'final-multi' : 'final'),
  ).reduce((mapping, question, questionIndex) => {
    if (hasElement({ type, index }, question.mapping)) {
      mapping.push(questionIndex + 1)
    }
    return mapping
  }, [])

const getRows = (unit, sectionIndex) => {
  const section = getSection(unit, sectionIndex)
  const mapping = getAllSectionMapping(section)
  return mapping.reduce((rows, element) => {
    const rowData = {
      learnerGuideContent: getLearnerGuideContentMapping(
        section,
        sectionIndex,
        element.type,
        element.index,
      ),
      reviewQuestions: getFormativeTheoryAssessmentMapping(section, sectionIndex, element.type, element.index, false),
      reviewQuestionsMulti: getFormativeTheoryAssessmentMapping(section, sectionIndex, element.type, element.index, true),
      finalTheoryQuestions: getSummativeTheoryAssessmentMapping(section, sectionIndex, element.type, element.index, false),
      finalTheoryQuestionsMulti: getSummativeTheoryAssessmentMapping(section, sectionIndex, element.type, element.index, true),
    }
    set(rows, `${element.type}.${element.index.join('.')}`, rowData)
    return rows
  }, {})
}

const MappedRow = ({
  unitElementDisplay,
  row: { learnerGuideContent, reviewQuestions, reviewQuestionsMulti, finalTheoryQuestions, finalTheoryQuestionsMulti },
}) => {
  return (
    <tr>
      <td>
        {unitElementDisplay}
      </td>
      <td>
        {learnerGuideContent.join(', ') || '-'}
      </td>
      <td>
        {reviewQuestions.join(', ') || '-'}
      </td>
      <td>
        {reviewQuestionsMulti.join(', ') || '-'}
      </td>
      <td>
        {finalTheoryQuestions.join(', ') || '-'}
      </td>
      <td>
        {finalTheoryQuestionsMulti.join(', ') || '-'}
      </td>
    </tr>
  )
}

const unitElementOrder = [
  'performanceCriteria',
  'performanceEvidence',
  'knowledgeEvidence',
]

const MappingFooter = ({ unit, sectionIndex }) =>
  <Container>
    <Table>
      <thead>
        <tr>
          <th>Unit Element</th>
          <th>LG Content</th>
          <th>Review Q - Short</th>
          <th>Review Q - Multi</th>
          <th>Final Theory Q - Short</th>
          <th>Final Theory Q - Multi</th>
        </tr>
      </thead>
      <tbody>
        {flatten(
          [...Object.entries(getRows(unit, sectionIndex))]
            .sort(
              ([a], [b]) =>
                unitElementOrder.indexOf(a) > unitElementOrder.indexOf(b)
                  ? 1
                  : unitElementOrder.indexOf(a) < unitElementOrder.indexOf(b)
                    ? -1
                    : 0,
            )
            .map(([type, row]) =>
              row.map(
                (row, i) =>
                  !Array.isArray(row)
                    ? <MappedRow
                        key={`${type}-${i}`}
                        unitElementDisplay={
                          <UnitElement type={type} index={[i]} />
                        }
                        row={row}
                      />
                    : [
                        <tr key={`${type}-${i}-row`}>
                          <td colSpan={4}>
                            <span>
                              <b>
                                {
                                  {
                                    performanceEvidence: 'PE',
                                    performanceCriteria: 'PC',
                                    knowledgeEvidence: 'KE',
                                  }[type]
                                }{' '}
                              </b>
                              <span>
                                { unit[type][i] 
                                  ? unit[type][i].text
                                  : 'MISSING' }
                              </span>
                            </span>
                          </td>
                        </tr>,
                        row.map(
                          (row, j) =>
                            !Array.isArray(row)
                              ? <MappedRow
                                  key={`${type}-${i}-${j}`}
                                  unitElementDisplay={
                                    <UnitElement type={type} index={[i, j]} />
                                  }
                                  row={row}
                                />
                              : [
                                  <tr key={`element-${type}-${i}-${j}`}>
                                    <td colSpan={4}>
                                      <UnitElement type={type} index={[i, j]} />
                                    </td>
                                  </tr>,
                                  row.map((row, k) =>
                                    <MappedRow
                                      key={`${type}-${i}-${j}-${k}`}
                                      unitElementDisplay={
                                        <UnitElement
                                          type={type}
                                          index={[i, j, k]}
                                        />
                                      }
                                      row={row}
                                    />,
                                  ),
                                ],
                        ),
                      ],
              ),
            ),
        )}
      </tbody>
    </Table>
  </Container>

export default MappingFooter



// WEBPACK FOOTER //
// ./src/Units/ManageUnit/LearnerGuide/SectionContent/MappingFooter.js